// frontend/src/pages/admin/AdminAnnouncementsPage.js
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Megaphone,
  Plus,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  X,
  Upload,
  Info,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import useAdminStore from '../../store/adminStore';
import useAuthStore from '../../store/authStore';
import axiosInstance from '../../utils/axiosConfig';

const AdminAnnouncementsPage = () => {
  const { user } = useAuthStore();
  const {
    announcements,
    announcementsPagination,
    isLoading,
    getAllAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
  } = useAdminStore();

  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    type: '',
    isActive: '',
    priority: '',
    search: '',
  });

  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    imageUrl: '',
    type: 'BANNER',
    priority: 'MEDIUM',
    targetAudience: { tiers: [], regions: [], userTypes: [] },
    cta: { text: '', url: '', deepLink: '', action: '' },
    isUrgent: false,
    startDate: '',
    endDate: '',
    promotionId: '',
    recurrence: 3600000,
  });

  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const RECURRENCE_OPTIONS = [
    { value: 0, label: 'Never (Show Once)' },
    { value: 60000, label: '1 Minute' },
    { value: 120000, label: '2 Minutes' },
    { value: 180000, label: '3 Minutes' },
    { value: 300000, label: '5 Minutes' },
    { value: 600000, label: '10 Minutes' },
    { value: 1800000, label: '30 Minutes' },
    { value: 3600000, label: '1 Hour' },
    { value: 7200000, label: '2 Hours' },
    { value: 21600000, label: '6 Hours' },
    { value: 43200000, label: '12 Hours' },
    { value: 86400000, label: '24 Hours' },
  ];

  useEffect(() => {
    loadAnnouncements();
  }, [filters]);

  const loadAnnouncements = async () => {
    try {
      const { page, limit, ...rest } = filters;
      const cleanFilters = {};
      if (rest.type) cleanFilters.type = rest.type;
      if (rest.isActive) cleanFilters.isActive = rest.isActive;
      if (rest.priority) cleanFilters.priority = rest.priority;
      if (rest.search) cleanFilters.search = rest.search;
      
      await getAllAnnouncements({ ...cleanFilters, page: page || 1, limit: limit || 10 });
    } catch (error) {
      console.error('Failed to load announcements:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      imageUrl: '',
      type: 'BANNER',
      priority: 'MEDIUM',
      targetAudience: { tiers: [], regions: [], userTypes: [] },
      cta: { text: '', url: '', deepLink: '', action: '' },
      isUrgent: false,
      startDate: '',
      endDate: '',
      promotionId: '',
      recurrence: 3600000,
    });
    setImagePreview('');
    setEditingAnnouncement(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      imageUrl: announcement.imageUrl || '',
      type: announcement.type,
      priority: announcement.priority,
      targetAudience: announcement.targetAudience || { tiers: [], regions: [], userTypes: [] },
      cta: announcement.cta || { text: '', url: '', deepLink: '', action: '' },
      isUrgent: announcement.isUrgent,
      startDate: announcement.startDate ? announcement.startDate.split('T')[0] : '',
      endDate: announcement.endDate ? announcement.endDate.split('T')[0] : '',
      promotionId: announcement.promotionId || '',
      recurrence: announcement.recurrence || 3600000,
    });
    if (announcement.imageUrl) {
      setImagePreview(announcement.imageUrl);
    }
    setShowModal(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, WEBP, GIF)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('image', file);
    setUploadingImage(true);

    try {
      const response = await axiosInstance.post('/marketplace/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data?.success && response.data?.data?.url) {
        setFormData(prev => ({ ...prev, imageUrl: response.data.data.url }));
        toast.success('Image uploaded successfully!');
      } else if (response.data?.url) {
        setFormData(prev => ({ ...prev, imageUrl: response.data.url }));
        toast.success('Image uploaded successfully!');
      } else {
        toast.error(response.data?.error?.message || 'Failed to upload image');
        setImagePreview('');
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      let errorMessage = 'Failed to upload image. Please try again.';
      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      setImagePreview('');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }

    const submitData = {
      title: formData.title,
      content: formData.content,
      imageUrl: formData.imageUrl || null,
      type: formData.type,
      priority: formData.priority,
      targetAudience: formData.targetAudience || null,
      isUrgent: formData.isUrgent,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
      promotionId: formData.promotionId || null,
      recurrence: formData.recurrence || 0,
    };

    if (formData.cta.text && formData.cta.text.trim() !== '') {
      const ctaData = {
        text: formData.cta.text,
        action: formData.cta.action || '',
      };

      if (formData.cta.action === 'OPEN_URL' && formData.cta.url) {
        ctaData.url = formData.cta.url;
      } else if (formData.cta.action === 'OPEN_DEEPLINK' && formData.cta.deepLink) {
        ctaData.deepLink = formData.cta.deepLink;
      }

      if (formData.cta.action === 'OPEN_URL' && !formData.cta.url) {
        toast.error('Please enter a URL for the CTA');
        return;
      }
      if (formData.cta.action === 'OPEN_DEEPLINK' && !formData.cta.deepLink) {
        toast.error('Please enter a deep link for the CTA');
        return;
      }
      if (formData.cta.action === 'OPEN_BUSINESS' && !formData.promotionId) {
        toast.error('Please link a promotion to open a business profile');
        return;
      }

      submitData.cta = ctaData;
    }

    try {
      if (editingAnnouncement) {
        await updateAnnouncement(editingAnnouncement.id, submitData);
        toast.success('Announcement updated successfully!');
      } else {
        await createAnnouncement(submitData);
        toast.success('Announcement created successfully!');
      }
      setShowModal(false);
      resetForm();
      await loadAnnouncements();
    } catch (error) {
      console.error('Failed to save announcement:', error);
      let errorMessage = 'Failed to save announcement';
      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        await deleteAnnouncement(id);
        toast.success('Announcement deleted successfully!');
        await loadAnnouncements();
      } catch (error) {
        console.error('Failed to delete announcement:', error);
        let errorMessage = 'Failed to delete announcement';
        if (error.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        toast.error(errorMessage);
      }
    }
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 10,
      type: '',
      isActive: '',
      priority: '',
      search: '',
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (announcementsPagination?.totalPages || 1)) {
      setFilters({ ...filters, page: newPage });
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setFilters({ ...filters, limit: newLimit, page: 1 });
  };

  const getTypeBadge = (type) => {
    const config = {
      BANNER: 'bg-purple-50 text-purple-700 border-purple-200',
      POPUP: 'bg-pink-50 text-pink-700 border-pink-200',
      ALERT: 'bg-red-50 text-red-700 border-red-200',
      INLINE: 'bg-blue-50 text-blue-700 border-blue-200',
      FEATURED: 'bg-amber-50 text-amber-700 border-amber-200',
    };
    return <span className={`px-1.5 py-0.5 text-xs font-medium rounded border ${config[type] || config.BANNER}`}>{type}</span>;
  };

  const getPriorityBadge = (priority) => {
    const config = {
      URGENT: 'bg-red-50 text-red-700 border-red-200',
      HIGH: 'bg-orange-50 text-orange-700 border-orange-200',
      MEDIUM: 'bg-blue-50 text-blue-700 border-blue-200',
      LOW: 'bg-gray-50 text-gray-700 border-gray-200',
    };
    return <span className={`px-1.5 py-0.5 text-xs font-medium rounded border ${config[priority] || config.MEDIUM}`}>{priority}</span>;
  };

  const getRecurrenceLabel = (recurrence) => {
    const option = RECURRENCE_OPTIONS.find(opt => opt.value === recurrence);
    return option ? option.label : 'Never (Show Once)';
  };

  const total = announcementsPagination?.total || 0;
  const currentPage = announcementsPagination?.page || 1;
  const totalPages = announcementsPagination?.totalPages || 1;
  const limit = announcementsPagination?.limit || 10;
  const startIndex = (currentPage - 1) * limit + 1;
  const endIndex = Math.min(currentPage * limit, total);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center">
            <div className="w-1 h-5 bg-gradient-to-b from-pink-400 to-pink-600 rounded-full mr-2" />
            <div>
              <h3 className="text-base font-semibold text-gray-800">Announcements</h3>
              <p className="text-xs text-gray-500">Create and manage app announcements</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-wrap gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-8 pr-2 py-1.5 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset border-0 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs w-36"
              />
            </div>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="px-2 py-1.5 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset border-0 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
            >
              <option value="">Types</option>
              <option value="BANNER">Banner</option>
              <option value="POPUP">Popup</option>
              <option value="ALERT">Alert</option>
              <option value="INLINE">Inline</option>
              <option value="FEATURED">Featured</option>
            </select>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="px-2 py-1.5 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset border-0 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
            >
              <option value="">Priority</option>
              <option value="URGENT">Urgent</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
              className="px-2 py-1.5 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset border-0 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
            >
              <option value="">Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <button
              onClick={clearFilters}
              className="px-2.5 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs"
            >
              Clear
            </button>
            <button
              onClick={openCreateModal}
              className="px-3 py-1.5 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-medium rounded-lg shadow-md shadow-pink-500/30 hover:shadow-lg transition-all flex items-center text-xs"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              New
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 text-primary-500 animate-spin" />
        </div>
      ) : announcements && announcements.length > 0 ? (
        <>
          <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-primary-100/50 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Recurrence</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Views</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-primary-100/50">
                  {announcements.map((announcement) => (
                    <tr key={announcement.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2">
                        <div className="flex items-center">
                          {announcement.imageUrl && (
                            <img src={announcement.imageUrl} alt="" className="h-8 w-8 rounded-lg object-cover mr-2" />
                          )}
                          <div>
                            <div className="text-xs font-medium text-gray-800">{announcement.title}</div>
                            <div className="text-[10px] text-gray-500 truncate max-w-xs">{announcement.content}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{getTypeBadge(announcement.type)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{getPriorityBadge(announcement.priority)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${
                          announcement.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-700 border-gray-200'
                        }`}>
                          {announcement.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {announcement.isUrgent && (
                          <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-red-50 text-red-700 border border-red-200 rounded">Urgent</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-[10px] text-gray-600">
                        {getRecurrenceLabel(announcement.recurrence)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{announcement.views || 0}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{announcement.clicks || 0}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-[10px] text-gray-500">
                        {announcement.startDate ? new Date(announcement.startDate).toLocaleDateString() : 'Now'}
                        {announcement.endDate && ` → ${new Date(announcement.endDate).toLocaleDateString()}`}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center space-x-1.5">
                          <button onClick={() => openEditModal(announcement)} className="p-1.5 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all" title="Edit">
                            <Edit className="h-3.5 w-3.5 text-blue-600" />
                          </button>
                          <button onClick={() => handleDelete(announcement.id)} className="p-1.5 bg-red-50 rounded-lg hover:bg-red-100 transition-all" title="Delete">
                            <Trash2 className="h-3.5 w-3.5 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {announcementsPagination && announcementsPagination.total > 0 && (
            <div className="flex flex-col md:flex-row items-center justify-between gap-3 bg-white rounded-xl p-3 shadow-neomorph border border-primary-100/30">
              <div className="flex items-center space-x-2">
                <label className="text-xs text-gray-600">Show:</label>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="px-1.5 py-1 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs text-gray-500">per page</span>
              </div>

              <span className="text-xs text-gray-500">
                Showing <span className="font-medium text-gray-800">{startIndex}</span> to{' '}
                <span className="font-medium text-gray-800">{endIndex}</span> of{' '}
                <span className="font-medium text-gray-800">{total}</span>
              </span>

              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || isLoading}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentPage === 1 || isLoading
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                  }`}
                  title="First"
                >
                  <ChevronsLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isLoading}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentPage === 1 || isLoading
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                  }`}
                  title="Previous"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>

                <div className="flex items-center space-x-0.5">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${
                          currentPage === pageNum
                            ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                            : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || isLoading}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentPage === totalPages || isLoading
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                  }`}
                  title="Next"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || isLoading}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentPage === totalPages || isLoading
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                  }`}
                  title="Last"
                >
                  <ChevronsRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-xl shadow-neomorph p-8 text-center border border-primary-100/30">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-50 shadow-neomorph-inset mb-3">
            <Megaphone className="h-8 w-8 text-pink-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-800 mb-1">No Announcements</h3>
          <p className="text-sm text-gray-500 mb-3">
            {filters.search || filters.type || filters.priority || filters.isActive
              ? 'Try adjusting your filters.'
              : 'Create your first announcement.'}
          </p>
          {(filters.search || filters.type || filters.priority || filters.isActive) && (
            <button
              onClick={clearFilters}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-all inline-flex items-center mr-2 text-xs"
            >
              Clear Filters
            </button>
          )}
          <button
            onClick={openCreateModal}
            className="px-3 py-1.5 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-medium rounded-lg shadow-md shadow-pink-500/30 hover:shadow-lg transition-all inline-flex items-center text-xs"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Create
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-600/50 backdrop-blur-sm" onClick={() => { setShowModal(false); resetForm(); }}></div>

            <div className="inline-block align-bottom bg-[#E8ECF5] rounded-xl shadow-neomorph px-4 pt-4 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-5 border border-primary-100/30">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-base font-semibold text-gray-800">
                  {editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
                </h3>
                <button onClick={() => { setShowModal(false); resetForm(); }} className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover">
                  <X className="h-3.5 w-3.5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    placeholder="Enter announcement title"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Content *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    rows="2"
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    placeholder="Enter announcement content"
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Image (Optional)</label>
                  <div className="flex items-start gap-3">
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Preview" className="h-20 w-20 object-cover rounded-lg border-2 border-primary-200 shadow-neomorph-inset" />
                        <button
                          type="button"
                          onClick={() => { setImagePreview(''); setFormData(prev => ({ ...prev, imageUrl: '' })); }}
                          className="absolute -top-1.5 -right-1.5 p-0.5 bg-red-500 rounded-full text-white hover:bg-red-600 shadow-md transition-all"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <div className={`flex flex-col items-center justify-center w-20 h-20 border-2 border-dashed rounded-lg transition-all ${
                          uploadingImage ? 'border-primary-300 bg-primary-50' : 'border-gray-300 hover:border-primary-500 hover:bg-primary-50'
                        }`}>
                          {uploadingImage ? (
                            <>
                              <RefreshCw className="h-5 w-5 text-primary-500 animate-spin" />
                              <span className="text-[10px] text-gray-500 mt-0.5">Uploading...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="h-6 w-6 text-gray-400" />
                              <span className="text-[10px] text-gray-400 mt-0.5">Upload</span>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleImageUpload(e.target.files[0]);
                            }
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                    <div className="flex-1">
                      <ul className="text-[10px] text-gray-400 space-y-0.5">
                        <li>• JPG, PNG, WEBP, GIF</li>
                        <li>• Max: 5MB</li>
                        <li>• 1200 x 628px recommended</li>
                      </ul>
                      {formData.imageUrl && (
                        <p className="text-[10px] text-green-600 mt-1 truncate">✓ Uploaded</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    >
                      <option value="BANNER">Banner</option>
                      <option value="POPUP">Popup</option>
                      <option value="ALERT">Alert</option>
                      <option value="INLINE">Inline</option>
                      <option value="FEATURED">Featured</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    >
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="URGENT">Urgent</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Start Date</label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">End Date</label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    />
                  </div>
                </div>

                {/* Recurrence Field */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Recurrence</label>
                  <select
                    value={formData.recurrence}
                    onChange={(e) => setFormData(prev => ({ ...prev, recurrence: parseInt(e.target.value) }))}
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                  >
                    {RECURRENCE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-gray-400 mt-0.5">How long before showing again after dismissal</p>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isUrgent}
                      onChange={(e) => setFormData(prev => ({ ...prev, isUrgent: e.target.checked }))}
                      className="h-3.5 w-3.5 text-pink-600 focus:ring-pink-500 border-gray-300 rounded"
                    />
                    <span className="ml-1.5 text-xs text-gray-700">Urgent (sends push notification)</span>
                  </label>
                </div>

                {/* CTA Section */}
                <div className="border-t border-primary-100/30 pt-3 mt-1">
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">Call to Action (Optional)</h4>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Button Text</label>
                    <input
                      type="text"
                      value={formData.cta.text}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        cta: { ...prev.cta, text: e.target.value } 
                      }))}
                      className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                      placeholder="e.g., Learn More"
                    />
                  </div>

                  <div className="mt-2">
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Action</label>
                    <select
                      value={formData.cta.action}
                      onChange={(e) => {
                        const action = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          cta: {
                            ...prev.cta,
                            action: action,
                            url: action === 'OPEN_URL' ? prev.cta.url : '',
                            deepLink: action === 'OPEN_DEEPLINK' ? prev.cta.deepLink : '',
                          }
                        }));
                      }}
                      className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    >
                      <option value="">No Action</option>
                      <option value="OPEN_URL">🌐 Open URL</option>
                      <option value="OPEN_DEEPLINK">📱 Open Deep Link</option>
                      <option value="OPEN_BUSINESS">🏪 Open Business Profile</option>
                    </select>
                  </div>

                  {formData.cta.action === 'OPEN_URL' && (
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-700 mb-0.5">URL *</label>
                      <input
                        type="url"
                        value={formData.cta.url || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          cta: { ...prev.cta, url: e.target.value }
                        }))}
                        className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                        placeholder="https://example.com"
                      />
                    </div>
                  )}

                  {formData.cta.action === 'OPEN_DEEPLINK' && (
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-700 mb-0.5">Deep Link *</label>
                      <input
                        type="text"
                        value={formData.cta.deepLink || ''}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          cta: { ...prev.cta, deepLink: e.target.value }
                        }))}
                        className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                        placeholder="fastel://shop/product/123"
                      />
                    </div>
                  )}

                  {formData.cta.action === 'OPEN_BUSINESS' && (
                    <div className="mt-2 bg-blue-50 rounded-lg p-2 border border-blue-200">
                      <p className="text-xs text-blue-700 flex items-start">
                        <Info className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 mt-0.5" />
                        <span>
                          Opens the business profile linked to this announcement.
                          {!formData.promotionId && (
                            <span className="block mt-0.5 text-amber-600">⚠️ No promotion linked.</span>
                          )}
                          {formData.promotionId && (
                            <span className="block mt-0.5 text-green-600">✅ Promotion linked.</span>
                          )}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 flex justify-end space-x-2">
                <button onClick={() => { setShowModal(false); resetForm(); }} className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-xs">
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading || !formData.title || !formData.content}
                  className="px-3 py-1.5 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-medium rounded-lg shadow-md shadow-pink-500/30 hover:shadow-lg transition-all disabled:opacity-50 text-xs"
                >
                  {isLoading ? 'Saving...' : editingAnnouncement ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnnouncementsPage;